import { Spacer, Text } from "@hackthenorth/north";
import { SquaresPlusIcon } from "@heroicons/react/24/outline";
import { TrashIcon } from "@heroicons/react/24/solid";
import { Button } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Dropzone, Icon, IMAGE_FILE_TYPES, Modal } from "src/shared/components";
import { HackerAPIFile } from "src/shared/utils/hackerapi";
import { useFileDropzone } from "src/views/hacker/rsvp/career/useFileDropzone";

import FooterActions from "../../../../footerActions";
import { MODAL_FIELDS } from "../../quest/constants";

export interface CreateModalProps {
  isOpen: boolean;
  onConfirm: (
    name: string,
    pointCost: number,
    quantityAvailable: number,
    userRedeptionLimit: number,
    successMessage: string,
    errorMessage: string,
    description?: string,
    imageUrl?: string
  ) => void;
  onCancel: () => void;
}
const CreatePrizeModal: React.FC<CreateModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
}) => {
  const field = MODAL_FIELDS["PRIZE"].create;
  const [name, setName] = useState("");
  const [points, setPoints] = useState<string | undefined>(undefined);
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(1);
  const [inventoryAmount, setInventoryAmount] = useState<string | undefined>(
    undefined
  );
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [imageFile, setImageFile] = useState<HackerAPIFile[] | undefined>(
    undefined
  );

  const ACCEPTED_FILE_TYPES = IMAGE_FILE_TYPES;

  const { dropzoneInputRef, addFile, onFileError, setDropzoneErrorMessage } =
    useFileDropzone(
      imageFile?.[0] ?? undefined,
      (files) => {
        setImageFile(files);
      },
      ACCEPTED_FILE_TYPES
    );

  const baseFormValues = {
    name,
    isAvailable,
    limit,
    inventoryAmount,
    description,
    points,
  };

  const hasNonEmptyRequiredFields =
    baseFormValues.name &&
    baseFormValues.isAvailable !== undefined &&
    baseFormValues.limit > 0 &&
    baseFormValues.inventoryAmount &&
    baseFormValues.points;

  const resetFields = () => {
    setName("");
    setPoints(undefined);
    setIsAvailable(false);
    setLimit(1);
    setInventoryAmount(undefined);
    setDescription(undefined);
    setImageFile(undefined);
  };

  const handleConfirmation = () => {
    if (hasNonEmptyRequiredFields) {
      onConfirm(
        name,
        points ? Number(points) : 0,
        inventoryAmount ? Number(inventoryAmount) : 0,
        limit,
        field.success,
        field.error,
        description,
        imageFile && imageFile.length > 0 ? imageFile[0].uri : undefined
      );
      resetFields();
    }
  };

  const handleAvailableChange = (input: string) => {
    if (input === "AVAILABLE") {
      setInventoryAmount("1");
      setIsAvailable(true);
    } else if (input === "SOLD_OUT") {
      setInventoryAmount("0");
      setIsAvailable(false);
    }
  };

  useEffect(() => {
    if (inventoryAmount && Number(inventoryAmount) > 0 && !isAvailable) {
      setIsAvailable(true);
    }
  }, [inventoryAmount, isAvailable]);

  return (
    <Modal isOpen={isOpen}>
      <HeaderContainer>
        <HeaderIcon>
          <SquaresPlusIcon />
        </HeaderIcon>
        <Text mods="h3 bold">{field.title}</Text>
      </HeaderContainer>
      <FormContainer>
        <FirstLayer>
          <NameWrapper>
            <Text mods="body">{field.name}</Text>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={field.name_placeholder}
              style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
            />
          </NameWrapper>
          <DropdownWrapper>
            <Text mods="body">{field.type}</Text>
            <select
              value={isAvailable ? "AVAILABLE" : "SOLD_OUT"}
              onChange={(e) => handleAvailableChange(e.target.value)}
              onBlur={(e) => handleAvailableChange(e.target.value)}
              style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
            >
              {(
                field.type_options as Array<{ label: string; value: string }>
              ).map((opt, i) => (
                <option key={i} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </DropdownWrapper>
        </FirstLayer>

        <SecondLayer>
          <DescriptionWrapper>
            <Text mods="body">Prize Image</Text>
            {imageFile && imageFile.length > 0 ? (
              <ImageConfirmationWrapper>
                <ImageIconWrapper>
                  <Icon name="file-cloud" size="16px" />
                  <Text
                    mods="grey caption lh-regular"
                    className="file-dropzone-text"
                  >
                    {imageFile[0].name ?? "Uploaded Image"}
                  </Text>
                </ImageIconWrapper>
                <Button
                  color="tertiary"
                  trailingIcon={<TrashIcon width={16} height={16} />}
                  onClick={() => setImageFile(undefined)}
                />
              </ImageConfirmationWrapper>
            ) : (
              <Dropzone
                label={
                  <span>
                    <b>Upload an image</b> or drag and drop
                  </span>
                }
                acceptedFileTypes={IMAGE_FILE_TYPES}
                onFilesUpload={addFile}
                onError={onFileError}
                ref={dropzoneInputRef}
                onBlur={() => setDropzoneErrorMessage("")}
                max={1}
              />
            )}
          </DescriptionWrapper>
          <DescriptionWrapper>
            <Text mods="body">Description</Text>
            <DescriptionTextarea
              placeholder="Give a short 1-2 sentence description..."
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </DescriptionWrapper>
        </SecondLayer>

        <ThirdLayer>
          <CounterWrapper>
            <Text mods="body">Maximum redeemable times</Text>
            <CounterContainer>
              <CounterButton
                onClick={() => setLimit((prev) => (prev > 1 ? prev - 1 : prev))}
              >
                −
              </CounterButton>
              <CounterValue>{limit}</CounterValue>
              <CounterButton onClick={() => setLimit((prev) => prev + 1)}>
                +
              </CounterButton>
            </CounterContainer>
          </CounterWrapper>
          <NumberWrapper>
            <Text mods="body">{field.count_input}</Text>
            <input
              type="number"
              value={inventoryAmount}
              onChange={(e) => setInventoryAmount(e.target.value)}
              placeholder={field.count_input_placeholder}
              style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
            />
          </NumberWrapper>
          <NumberWrapper>
            <Text mods="body">{field.num_input}</Text>
            <input
              type="number"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              placeholder={field.num_input_placeholder}
              style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
            />
          </NumberWrapper>
        </ThirdLayer>
      </FormContainer>
      <Spacer height={36} />
      <FooterActions
        confirmText={"Create"}
        onConfirm={handleConfirmation}
        onCancel={onCancel}
        isConfirmDisabled={!hasNonEmptyRequiredFields}
      />
    </Modal>
  );
};

export default CreatePrizeModal;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 24px;
  gap: 8px;
`;

const HeaderIcon = styled.div<{ backgroundColor?: string }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 50%;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FirstLayer = styled.div`
  display: flex;
  gap: 20px;
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const SecondLayer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageIconWrapper = styled.div`
  width: 100%;
  align-items: center;
  padding: 8px;
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const ImageConfirmationWrapper = styled.div`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  display: flex;
  flex-direction: row;
  gap: 8px;
  border-radius: 8px;
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundGray};
`;

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;

  &::placeholder {
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
  }
`;

const ThirdLayer = styled.div`
  display: flex;
  gap: 20px;
`;

const CounterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const CounterContainer = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  overflow: hidden;
`;

const CounterButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};

  &:hover {
    background-color: ${({ theme }) =>
      theme.globalConstants.color.backgroundGray};
  }
`;

const CounterValue = styled.div`
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  border-right: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundLight};
`;

const NumberWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;
